import { useEffect, useRef } from 'react';
import { PdaScanEvent, useClearPdaScanQueue, useEnqueuePdaScan, usePdaPumpTick, usePdaScanQueueVersion, useSetPumpTick, useTakeNextPdaScan } from './Pda';

export interface ScanQueueConsumerOptions {
    /** 最大并发数，默认 1（串行） */
    maxConcurrency?: number;
    /** 处理单个扫描事件的回调，返回 Promise */
    onProcess: (event: PdaScanEvent) => Promise<void>;
}

export function useScanQueueConsumer(options: ScanQueueConsumerOptions) {
    const { maxConcurrency = 1, onProcess } = options;

    const enqueueScan = useEnqueuePdaScan();
    const queueVersion = usePdaScanQueueVersion();
    const pumpTick = usePdaPumpTick();
    const takeNextScan = useTakeNextPdaScan();
    const setPumpTick = useSetPumpTick();
    const clearQueue = useClearPdaScanQueue();

    const isPumpingRef = useRef(false);
    const inFlightRef = useRef<Set<string>>(new Set());
    const isStaleRef = useRef(false);
    const onProcessRef = useRef(onProcess);
    onProcessRef.current = onProcess;

    // 挂载时清空旧队列，卸载时标记失效
    useEffect(() => {
        isStaleRef.current = false;
        clearQueue();
        return () => {
            isStaleRef.current = true;
        };
    }, [clearQueue]);

    useEffect(() => {
        if (isPumpingRef.current) return;
        if (isStaleRef.current) return;
        isPumpingRef.current = true;

        const inFlight = inFlightRef.current;

        while (inFlight.size < maxConcurrency) {
            const next = takeNextScan();
            if (next == null) break;

            inFlight.add(next.id);
            void onProcessRef.current(next)
                .finally(() => {
                    inFlight.delete(next.id);
                    if (!isStaleRef.current) {
                        setPumpTick();
                    }
                });
        }

        isPumpingRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queueVersion, pumpTick, takeNextScan, maxConcurrency]);

    return { enqueueScan };
}
